import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  AlertCircle,
  ArrowLeft,
  ChevronDown,
  ChevronRight,
  X,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { gql, GraphQLError } from "../lib/api";
import {
  CREATE_APPLICATION_MUTATION,
  CREATE_AND_SEND_INTAKE_MUTATION,
  SAVE_DRAFT_APPLICATION_MUTATION,
} from "../lib/operations";
import { ErrorMessage } from "../components/ErrorMessage";
import { selectPricingTier } from "../lib/pricingTiers";
import { SalesCodeSelect } from "../components/SalesCodeSelect";
import { templateIdForSalesCode } from "../lib/salesCode";
import type {
  AddressInput,
  ApplicationResult,
  CreateApplicationInput,
  IntakeRecipientType,
  MerchantDataInput,
  PdiPrefsInput,
  SendIntakeLinkInput,
} from "../lib/types";
import { mnspShippingToPdiLabel } from "@/lib/mnspPricing";
import { PercentageTotalIndicator } from "@/components/PercentageTotalIndicator";
import {
  DocumentUploadRow,
  EMPTY_MNSP_FORM,
  MnspEnrollmentSection,
  toWireMnsp,
  type MnspEnrollmentFormState,
} from "@/components/MnspEnrollmentSection";
import { isOtherPosPlatform } from "@/lib/posPlatforms";
import {
  validateCreateApplication,
  validateSaveDraft,
  validateSendIntake,
} from "@/lib/applicationValidation";
import { useFieldErrors } from "@/lib/useFieldErrors";
import { operatorEnteredPaths } from "@/lib/intakeLocks";
import {
  DOCUMENT_KINDS,
  documentFileProblem,
  documentLabel,
  uploadApplicationDocuments,
  type DocumentKind,
  type PendingDocuments,
} from "@/lib/applicationDocuments";
import {
  APPLICATION_OPTIONS,
  applicationOptionFor,
  pdiOrderDefaultForOption,
  productsForOption,
  rentalIsOptional,
  type ApplicationOption,
} from "@/lib/applicationOptions";

import {
  AddressFields,
  annualSecurityBundleFeeInvalid,
  authFeeOverrideInvalid,
  BankingFields,
  buildCloverSecurityAndTransarmorInput,
  buildFeeOverridesInput,
  buildFleetOverridesInput,
  buildIcPlusPricingOverridesInput,
  BusinessDetailsFields,
  CloverSecurityAndTransarmorSection,
  EMPTY_ADDRESS,
  EMPTY_BANK,
  EMPTY_BUSINESS,
  EMPTY_CLOVER_SECURITY_AND_TRANSARMOR,
  EMPTY_FEE_OVERRIDES,
  EMPTY_FLEET_OVERRIDES,
  EMPTY_IC_PLUS_PRICING_OVERRIDES,
  EMPTY_MODE,
  EMPTY_OWNER,
  EMPTY_VOLUME,
  FleetPricingAdjustmentSection,
  IS_DEV_API,
  ManualFeeAdjustmentSection,
  OwnershipFields,
  parseAnnualSecurityBundleFee,
  parseAuthFeeOverride,
  patch,
  PdiFields,
  SameAsToggle,
  Section,
  FieldErrorText,
  TF,
  TransactionMixFields,
  VolumeFields,
  type AddressForm,
  type BankForm,
  type BusinessForm,
  type CloverSecurityAndTransarmorForm,
  type FeeOverridesForm,
  type FieldError,
  type FleetOverridesForm,
  type IcPlusPricingOverridesForm,
  type ModeForm,
  type OwnerForm,
  type VolumeDetailsForms,
} from "@/components/application";

// Template used when the operator leaves the sales code on "Auto" -- the
// same template CCIP-WILMA-AI (the above-threshold generic-Wilma portfolio)
// pairs with, so an Auto application boards identically whether the backend
// routes it to WILMA-AI or the operator picks WILMA-AI explicitly. Picking
// a specific sales code swaps this for its paired template (see
// templateIdForSalesCode).
const DEFAULT_TEMPLATE_ID = IS_DEV_API ? "52971" : "275113";

const PDI_DEFAULTS: PdiPrefsInput = {
  // "" = no explicit choice yet -> PdiFields shows the path-aware default
  // (Yes for bundled, No for enrollment-only) and pdiOrderHandler applies the
  // same default; an explicit "Yes"/"No" is written once the operator picks.
  sendPdiOrder: "",
  posPlatform: "",
  singleOrMultiSite: "Single",
  gasPumpYn: "No",
  gasPumpType: "",
  broadbandYn: "No",
  managedWifiInterest: "",
  residentialLocation: "No",
  shippingSpeed: "Standard",
  notes: "",
};

// Form-local state shapes (all strings — inputs are strings; numerics are
// coerced when we assemble the typed CreateApplicationInput on submit).

type DeliveryPercentagesForm = {
  dlvrySameDayPct: string;
  dlvry0To7DaysPct: string;
  dlvry8To14DaysPct: string;
  dlvry15To30DaysPct: string;
  dlvryOver30DaysPct: string;
};

const EMPTY_DELIVERY: DeliveryPercentagesForm = {
  dlvrySameDayPct: "100",
  dlvry0To7DaysPct: "0",
  dlvry8To14DaysPct: "0",
  dlvry15To30DaysPct: "0",
  dlvryOver30DaysPct: "0",
};

type RegisteringTechForm = {
  assignedTechEmail: string;
  assignedToPIN: string;
  registeringFirstName: string;
  registeringLastName: string;
  registeringPhone: string;
};

const EMPTY_REGISTERING_TECH: RegisteringTechForm = {
  assignedTechEmail: "",
  assignedToPIN: "",
  registeringFirstName: "",
  registeringLastName: "",
  registeringPhone: "",
};

// Send-intake-only form state. Everything the two form paths had in common
// (business name / contact / salesperson / MNSP + terminal-rental flags) now
// lives in the shared `biz` / `registeringTech` / `mnspEnrollment` /
// `terminalRental` state instead, so this only carries fields that are
// meaningless when the operator fills + submits the application directly.
type SendForm = {
  recipientType: IntakeRecipientType;
  recipientEmail: string;
  ccEmail: string;
  expiresInDays: string;
  personalMessage: string;
  autoSubmit: boolean;
};

// localStorage draft snapshot. Carries every piece of form state needed
// to fully restore the page after a tab close. Sensitive fields are
// stored masked to last-4 (operator re-types on restore — see comment
// in NewApplication on the autosave block).
interface NewApplicationDraft {
  v: 1;
  savedAt: string; // ISO
  biz: BusinessForm;
  bizAddr: AddressForm;
  mailAddr: AddressForm;
  mailSame: boolean;
  owner: OwnerForm;
  ownerAddr: AddressForm;
  ownerAddrSame: boolean;
  deposit: BankForm;
  withdrawal: BankForm;
  withdrawalSame: boolean;
  mode: ModeForm;
  vol: VolumeDetailsForms;
  deliveryPercentages: DeliveryPercentagesForm;
  pdiPrefs: PdiPrefsInput;
  sendForm: SendForm;
  sendMode: "fill" | "send";
  // Optional: drafts saved before the MNSP/terminal-rental flags were unified
  // omit these (older drafts stored them per-path as requireMNSP /
  // sendForm.mnspBundle); default to true on restore, matching the state.
  mnspEnrollment?: boolean;
  terminalRental?: boolean;
  // Optional: drafts saved before the sales-code picker existed omit this
  // ("" = Auto, the state's own default).
  salesCode?: string;
  // Optional: drafts saved before this existed default to BUNDLED on restore.
  applicationPath?: "BUNDLED" | "MNSP_ENROLLMENT_ONLY";
  registeringTech: RegisteringTechForm;
  // Optional: drafts saved before the auth-fee override existed omit this.
  authFeeOverride?: string;
  // Optional: drafts saved before the Annual security bundle fee existed omit this.
  annualSecurityBundleFee?: string;
  // Optional: drafts saved before manual fee overrides existed omit this.
  feeOverrides?: FeeOverridesForm;
  // Optional: drafts saved before icPlusPricing overrides existed omit this.
  icPlusPricingOverrides?: IcPlusPricingOverridesForm;
  // Optional: drafts saved before Clover Security + TransArmor overrides
  // existed omit this.
  cloverSecurityAndTransarmor?: CloverSecurityAndTransarmorForm;
  // Optional: drafts saved before fleet overrides existed omit this.
  fleetOverrides?: FleetOverridesForm;
  // Optional: drafts saved before internal notes existed omit this.
  internalNotes?: string;
}

const DRAFT_MAX_AGE_MS = 24 * 60 * 60 * 1000;
const DRAFT_DEBOUNCE_MS = 1000;
// Hours-old retention guardrail. Older than this and we silently
// discard rather than even offering restore — a 3-day-old draft is
// almost certainly noise from a session the operator forgot about.

function last4Mask(raw: string | undefined | null): string {
  const v = (raw || "").replace(/\D/g, "");
  return v.length > 4 ? `****${v.slice(-4)}` : "";
}

function redactForDraft(d: NewApplicationDraft): NewApplicationDraft {
  // Defensive deep-redact — never echo a raw SSN / EIN / account / routing
  // through localStorage. Operator re-types these on restore.
  return {
    ...d,
    biz: { ...d.biz, taxID: last4Mask(d.biz.taxID) },
    owner: {
      ...d.owner,
      ssn: last4Mask(d.owner.ssn),
      driversLicenseNumber: last4Mask(d.owner.driversLicenseNumber),
    },
    deposit: {
      ...d.deposit,
      accountNumber: last4Mask(d.deposit.accountNumber),
      confirmAccountNumber: last4Mask(d.deposit.confirmAccountNumber),
      routingNumber: last4Mask(d.deposit.routingNumber),
    },
    withdrawal: {
      ...d.withdrawal,
      accountNumber: last4Mask(d.withdrawal.accountNumber),
      confirmAccountNumber: last4Mask(d.withdrawal.confirmAccountNumber),
      routingNumber: last4Mask(d.withdrawal.routingNumber),
    },
  };
}

// Strip the leading "****" prefix from any sensitive-field placeholder
// on restore. The operator will be prompted to re-enter — empty string
// is the right starting state, not a phantom mask the validator would
// pass through to the wire.
function unmaskForRestore(d: NewApplicationDraft): NewApplicationDraft {
  const unmask = (s: string) => (s.startsWith("****") ? "" : s);
  return {
    ...d,
    biz: { ...d.biz, taxID: unmask(d.biz.taxID) },
    owner: {
      ...d.owner,
      ssn: unmask(d.owner.ssn),
      driversLicenseNumber: unmask(d.owner.driversLicenseNumber),
    },
    deposit: {
      ...d.deposit,
      accountNumber: unmask(d.deposit.accountNumber),
      confirmAccountNumber: unmask(d.deposit.confirmAccountNumber),
      routingNumber: unmask(d.deposit.routingNumber),
    },
    withdrawal: {
      ...d.withdrawal,
      accountNumber: unmask(d.withdrawal.accountNumber),
      confirmAccountNumber: unmask(d.withdrawal.confirmAccountNumber),
      routingNumber: unmask(d.withdrawal.routingNumber),
    },
  };
}

// Realistic sample data for the preview/demo "Load sample data" button.
// `satisfies` (not `as`) so a form field added later that's missing here is a
// compile error, not a silently-undefined value at runtime -- see the
// ownershipTypeCd omission that crashed ownerTitleMismatch (2026-09-01).
const SAMPLE = {
  business: {
    businessName: "Acme C-Store LLC",
    dbaName: "Acme Mart",
    taxFilingName: "Acme C-Store LLC",
    taxFilingMethod: "EIN",
    taxID: "485968574",
    mcc: "5541",
    storeType: "fuelAndConvienienceStation",
    businessStartDate: "01/01/2010",
    businessDescription: "Convenience store with fuel",
    website: "https://acmemart.example",
    contactEmail: "owner@acmemart.example",
    contactPhone: "360-555-0100",
  } satisfies BusinessForm,
  address: {
    address1: "100 Main St",
    address2: "",
    city: "Keystone",
    stateCd: "CO",
    zip: "80435",
    countryCd: "US",
  } satisfies AddressForm,
  owner: {
    firstName: "Dana",
    lastName: "Operator",
    title: "MEMBER_LLC",
    email: "dana@acmemart.example",
    phone: "360-555-0101",
    ssn: "456-45-6789",
    dateOfBirth: "01/02/1980",
    ownershipPct: "100",
    ownershipTypeCd: "LLC",
    driversLicenseNumber: "D1234567",
    driversLicenseStateCd: "CO",
  } satisfies OwnerForm,
  bank: {
    accountNumber: "000123456789",
    confirmAccountNumber: "000123456789",
    routingNumber: "987654321",
    accountType: "BIZ",
    bankName: "First Bank",
  } satisfies BankForm,
  mode: {
    eCommercePct: "0",
    keyedPct: "10",
    mailOrderPct: "0",
    swipedPct: "90",
  } satisfies ModeForm,
};

function toIntOrUndef(v: string): number | undefined {
  const n = parseInt(v, 10);
  return Number.isFinite(n) ? n : undefined;
}

function trimAddress(a: AddressForm): AddressInput | undefined {
  const out: AddressInput = {
    address1: a.address1.trim() || undefined,
    address2: a.address2.trim() || undefined,
    city: a.city.trim() || undefined,
    stateCd: a.stateCd.trim() || undefined,
    zip: a.zip.trim() || undefined,
    countryCd: a.countryCd.trim() || undefined,
  };
  return Object.values(out).some(Boolean) ? out : undefined;
}

// ----- page ------------------------------------------------------------------

export function NewApplication({ preview = false }: { preview?: boolean }) {
  const navigate = useNavigate();

  const [biz, setBiz] = useState<BusinessForm>(EMPTY_BUSINESS);
  const [bizAddr, setBizAddr] = useState<AddressForm>(EMPTY_ADDRESS);
  const [mailAddr, setMailAddr] = useState<AddressForm>(EMPTY_ADDRESS);
  // "Same as business address" starts unchecked: the operator opts in.
  const [mailSame, setMailSame] = useState(false);
  const [owner, setOwner] = useState<OwnerForm>(EMPTY_OWNER);
  const [ownerAddr, setOwnerAddr] = useState<AddressForm>(EMPTY_ADDRESS);
  const [ownerAddrSame, setOwnerAddrSame] = useState(false);
  const [deposit, setDeposit] = useState<BankForm>(EMPTY_BANK);
  const [withdrawal, setWithdrawal] = useState<BankForm>(EMPTY_BANK);
  const [withdrawalSame, setWithdrawalSame] = useState(true);
  const [mode, setMode] = useState<ModeForm>(EMPTY_MODE);
  const [pdiPrefs, setPdiPrefs] = useState<PdiPrefsInput>(PDI_DEFAULTS);
  const [pdiOpen, setPdiOpen] = useState(true);
  const [posOtherSelected, setPosOtherSelected] = useState(false);
  const [gasOtherSelected, setGasOtherSelected] = useState(false);
  const posOtherMode =
    posOtherSelected || isOtherPosPlatform(pdiPrefs.posPlatform);
  const [vol, setVol] = useState<VolumeDetailsForms>(EMPTY_VOLUME);
  // Operator-selected CardConnect sales code (#64). "" = Auto: the backend
  // defaults it by the pricing threshold from the merchant's volume. Set via
  // the SalesCodeSelect picker in the "CardConnect portfolio" section.
  const [salesCode, setSalesCode] = useState<string>("");
  // Template that pairs with the selected sales code -- swaps with the
  // picker on create (see templateIdForSalesCode); Auto keeps the default.
  const derivedTemplateId =
    templateIdForSalesCode(salesCode, { dev: IS_DEV_API }) ??
    DEFAULT_TEMPLATE_ID;
  // Operator per-application auth-fee override (special-merchant pricing).
  // "" = no override (pure tiered). Shared across both the fill and send
  // forms since they are mutually exclusive (sendMode). Dollars, 0..10.
  const [authFeeOverride, setAuthFeeOverride] = useState<string>("");
  // Operator override of the Annual security bundle fee. "" = the $199 tier
  // default. CardConnect has no API field for it, so this is persisted to our
  // DB only and never boarded (same as fleetOverrides). Dollars.
  const [annualSecurityBundleFee, setAnnualSecurityBundleFee] =
    useState<string>("");
  // Admin-only manual overrides for every other fee on the tier's fees
  // block (Manual fee adjustment section). Shared across both forms same
  // as authFeeOverride above. Blank fields keep the tier default.
  const [feeOverrides, setFeeOverrides] =
    useState<FeeOverridesForm>(EMPTY_FEE_OVERRIDES);
  // Admin-only manual overrides for the icPlusPricing block (same Manual
  // fee adjustment section). Shared across both forms same as feeOverrides.
  const [icPlusPricingOverrides, setIcPlusPricingOverrides] =
    useState<IcPlusPricingOverridesForm>(EMPTY_IC_PLUS_PRICING_OVERRIDES);
  // Admin-only Clover Security + TransArmor overrides. A THIRD parallel
  // state to feeOverrides/icPlusPricingOverrides; shared across both forms
  // same as above. Blank fields keep the tier default. Unlike fleetOverrides
  // below, this DOES reach the CardConnect payload.
  const [cloverSecurityAndTransarmor, setCloverSecurityAndTransarmor] =
    useState<CloverSecurityAndTransarmorForm>(
      EMPTY_CLOVER_SECURITY_AND_TRANSARMOR,
    );
  // Admin-only fleet-pricing overrides (Fleet pricing adjustment section).
  // SEPARATE parallel state to feeOverrides; shared across both forms same as
  // above. Blank fields keep the tier fleet default. Persisted for future
  // MAR/display only, never sent to CardConnect.
  const [fleetOverrides, setFleetOverrides] = useState<FleetOverridesForm>(
    EMPTY_FLEET_OVERRIDES,
  );
  // Operator-only free-text notes (pricing context, special arrangements,
  // etc.) attached directly to the application row -- unlike sendForm.
  // personalMessage (a one-time note baked into the intake email, which the
  // merchant DOES see), this is persisted, editable after creation via
  // EditApplicationModal, and never surfaced to the merchant anywhere
  // (issue #130). Shared across both forms same as the pricing overrides
  // above.
  const [internalNotes, setInternalNotes] = useState<string>("");
  const [deliveryPercentages, setDeliveryPercentages] =
    useState<DeliveryPercentagesForm>(EMPTY_DELIVERY);
  const [registeringTech, setRegisteringTech] = useState<RegisteringTechForm>(
    EMPTY_REGISTERING_TECH,
  );

  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  // "Save as draft": persists the row without submitting to CardConnect or
  // sending an intake link. Separate from `loading` so both buttons can show
  // their own busy state.
  const [savingDraft, setSavingDraft] = useState(false);
  const [previewJson, setPreviewJson] = useState<string | null>(null);
  const errorBannerRef = useRef<HTMLDivElement>(null);

  // "fill" = operator completes the whole application and it goes straight to
  // CardConnect; "send" = operator emails a tokenized intake link and the
  // recipient completes it. Both live in one form now (see the "How do you
  // want to do this?" section); preview defaults to "fill" since that route
  // has no option selector.
  const [sendMode, setSendMode] = useState<"fill" | "send">(
    preview ? "fill" : "send",
  );
  const [applicationPath, setApplicationPath] = useState<
    "BUNDLED" | "MNSP_ENROLLMENT_ONLY"
  >("BUNDLED");
  const [sendForm, setSendForm] = useState<SendForm>({
    recipientType: "MERCHANT" as IntakeRecipientType,
    recipientEmail: "",
    ccEmail: "",
    expiresInDays: "30",
    personalMessage: "",
    autoSubmit: false,
  });
  const [sendError, setSendError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);

  const [mnspForm, setMnspForm] =
    useState<MnspEnrollmentFormState>(EMPTY_MNSP_FORM);
  const [mnspError, setMnspError] = useState<string | null>(null);
  // MNSP network enrollment (MAR + End User Agreement) and terminal-equipment
  // rental. Both default on and both apply to whichever delivery path is
  // chosen -- buildInput() forces mnspEnrollment true for MNSP_ENROLLMENT_ONLY.
  const [mnspEnrollment, setMnspEnrollment] = useState(true);
  const [terminalRental, setTerminalRental] = useState(true);
  // The merchant's driver's license + voided check, optional on every path
  // (the employee filling this in may have neither). Held here until the
  // application exists, then uploaded by finishWithDocuments.
  const [documents, setDocuments] = useState<PendingDocuments>({});
  // Set when the application was created but a document upload failed: the
  // form stays put (with Retry / Continue) instead of navigating away, and
  // won't submit again -- the application already exists.
  const [documentUploadFailure, setDocumentUploadFailure] = useState<{
    applicationID: string;
    failed: DocumentKind[];
    go: () => void;
  } | null>(null);
  const [retryingDocuments, setRetryingDocuments] = useState(false);
  // The "Application path" picker's four options (CardConnect Only, CardConnect
  // + MAR, MNSP Enrollment Only, CardConnect + MNSP Bundle) are combinations of
  // the three fields above -- see lib/applicationOptions.ts -- so the option is
  // derived from them rather than stored, and picking one sets all three.
  const applicationOption = applicationOptionFor({
    applicationPath,
    mnspEnrollment,
    terminalRental,
  });
  function selectApplicationOption(option: ApplicationOption) {
    const next = productsForOption(option, {
      applicationPath,
      mnspEnrollment,
      terminalRental,
    });
    setApplicationPath(next.applicationPath);
    setMnspEnrollment(next.mnspEnrollment);
    setTerminalRental(next.terminalRental);
    // CardConnect Only / + MAR must not fire a PDI order by default (both are
    // BUNDLED, which otherwise defaults the order on); see
    // pdiOrderDefaultForOption.
    setPdiPrefs((prev) => ({
      ...prev,
      sendPdiOrder: pdiOrderDefaultForOption(option),
    }));
  }

  // ----- localStorage autosave (operator-side only) -----------------------
  // Recover-from-tab-close discipline. Operators sometimes spend 15-20 min
  // filling this; an accidental nav loses everything. Per-keystroke,
  // debounced 1s. Sensitive fields are redacted to last-4 before storage
  // (parity with the server's redact_merchant_for_storage) — recovered
  // drafts ask the operator to re-type SSN/EIN/account/routing, which is
  // an acceptable trade vs. echoing raw PII into localStorage.
  //
  // Disabled entirely in preview mode (no auth context, no benefit from
  // persisting a throwaway demo state).
  const auth = useAuth();
  const draftKey = useMemo(() => {
    if (preview) return "";
    const op = auth.decoded?.email || "anon";
    return `paymentproducts.newApp-draft.${op}`;
  }, [preview, auth.decoded?.email]);
  const [draftSavedAt, setDraftSavedAt] = useState<Date | null>(null);
  const [restorePrompt, setRestorePrompt] = useState<{
    savedAt: Date;
    snapshot: NewApplicationDraft;
  } | null>(null);
  const draftDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const draftHydrated = useRef(false);

  // When the user toggles the mailing-same / owner-same checkboxes
  // back ON, the mirrored address state stays at its prior value so a
  // later un-check restores what they typed. When they toggle the
  // checkbox OFF, we seed the mirrored address with the business
  // address so they don't have to retype from scratch (P1-12).
  useEffect(() => {
    if (!mailSame) {
      setMailAddr((prev) =>
        prev.address1 || prev.city || prev.stateCd || prev.zip
          ? prev
          : { ...bizAddr },
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mailSame]);
  useEffect(() => {
    if (!ownerAddrSame) {
      setOwnerAddr((prev) =>
        prev.address1 || prev.city || prev.stateCd || prev.zip
          ? prev
          : { ...bizAddr },
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ownerAddrSame]);

  // Draft restore-on-mount. Reads the prior session's snapshot and, if
  // it's recent (< 24h), shows a non-blocking banner offering to
  // restore. The banner stays until the operator picks Restore or
  // Discard — silently rehydrating would be too magic.
  useEffect(() => {
    if (!draftKey || draftHydrated.current) return;
    draftHydrated.current = true;
    try {
      const raw = localStorage.getItem(draftKey);
      if (!raw) return;
      const parsed = JSON.parse(raw) as NewApplicationDraft;
      if (!parsed || parsed.v !== 1) return;
      const savedAt = new Date(parsed.savedAt);
      if (
        isNaN(savedAt.getTime()) ||
        Date.now() - savedAt.getTime() > DRAFT_MAX_AGE_MS
      ) {
        localStorage.removeItem(draftKey);
        return;
      }
      setRestorePrompt({ savedAt, snapshot: parsed });
    } catch {
      // Corrupt draft — nuke it rather than perpetually re-prompting.
      try {
        localStorage.removeItem(draftKey);
      } catch {
        // localStorage unavailable; nothing to do.
      }
    }
  }, [draftKey]);

  // Draft autosave. Debounced 1s so rapid typing doesn't write to
  // localStorage on every keystroke. Skipped while the restore prompt
  // is up (would overwrite the candidate draft mid-decision) and in
  // preview mode (no draftKey).
  useEffect(() => {
    if (!draftKey || restorePrompt || preview) return;
    if (draftDebounceRef.current) clearTimeout(draftDebounceRef.current);
    draftDebounceRef.current = setTimeout(() => {
      try {
        const snapshot: NewApplicationDraft = {
          v: 1,
          savedAt: new Date().toISOString(),
          biz,
          bizAddr,
          mailAddr,
          mailSame,
          owner,
          ownerAddr,
          ownerAddrSame,
          deposit,
          withdrawal,
          withdrawalSame,
          mode,
          vol,
          deliveryPercentages,
          pdiPrefs,
          sendForm,
          sendMode,
          mnspEnrollment,
          terminalRental,
          salesCode,
          applicationPath,
          registeringTech,
          authFeeOverride,
          annualSecurityBundleFee,
          feeOverrides,
          icPlusPricingOverrides,
          cloverSecurityAndTransarmor,
          fleetOverrides,
          internalNotes,
        };
        localStorage.setItem(
          draftKey,
          JSON.stringify(redactForDraft(snapshot)),
        );
        setDraftSavedAt(new Date());
      } catch {
        // QuotaExceeded / private-mode / SSR — silently no-op. Operator
        // can still submit the form; we just lose the draft safety net.
      }
    }, DRAFT_DEBOUNCE_MS);
    return () => {
      if (draftDebounceRef.current) clearTimeout(draftDebounceRef.current);
    };
  }, [
    draftKey,
    restorePrompt,
    preview,
    biz,
    bizAddr,
    mailAddr,
    mailSame,
    owner,
    ownerAddr,
    ownerAddrSame,
    deposit,
    withdrawal,
    withdrawalSame,
    mode,
    vol,
    deliveryPercentages,
    pdiPrefs,
    sendForm,
    sendMode,
    mnspEnrollment,
    terminalRental,
    salesCode,
    applicationPath,
    registeringTech,
    authFeeOverride,
    annualSecurityBundleFee,
    feeOverrides,
    icPlusPricingOverrides,
    cloverSecurityAndTransarmor,
    fleetOverrides,
    internalNotes,
  ]);

  function discardDraft() {
    if (!draftKey) return;
    try {
      localStorage.removeItem(draftKey);
    } catch {
      // localStorage unavailable; nothing to do.
    }
    setDraftSavedAt(null);
  }

  function applyRestore() {
    if (!restorePrompt) return;
    const r = unmaskForRestore(restorePrompt.snapshot);
    setBiz(r.biz);
    setBizAddr(r.bizAddr);
    setMailAddr(r.mailAddr);
    setMailSame(r.mailSame);
    // Spread over EMPTY_OWNER like the override restores below: a draft saved
    // before a field existed (e.g. ownershipTypeCd) shouldn't leave it
    // undefined -- ownerTitleMismatch and friends expect a real string.
    setOwner({ ...EMPTY_OWNER, ...r.owner });
    setOwnerAddr(r.ownerAddr);
    setOwnerAddrSame(r.ownerAddrSame);
    setDeposit(r.deposit);
    setWithdrawal(r.withdrawal);
    setWithdrawalSame(r.withdrawalSame);
    setMode(r.mode);
    setVol(r.vol);
    setDeliveryPercentages(r.deliveryPercentages);
    // Spread over the defaults so a field added after the draft was saved
    // (e.g. sendPdiOrder) still has a defined value rather than undefined.
    setPdiPrefs({ ...PDI_DEFAULTS, ...r.pdiPrefs });
    if (r.sendForm)
      setSendForm({
        recipientType: r.sendForm.recipientType ?? "MERCHANT",
        recipientEmail: r.sendForm.recipientEmail ?? "",
        ccEmail: r.sendForm.ccEmail ?? "",
        expiresInDays: r.sendForm.expiresInDays ?? "30",
        personalMessage: r.sendForm.personalMessage ?? "",
        autoSubmit: r.sendForm.autoSubmit ?? false,
      });
    if (r.sendMode) setSendMode(r.sendMode);
    // Unified MNSP / terminal-rental flags: drafts saved before the unification
    // stored these per-path, so fall back to the state default (true).
    if (typeof r.mnspEnrollment === "boolean")
      setMnspEnrollment(r.mnspEnrollment);
    if (typeof r.terminalRental === "boolean")
      setTerminalRental(r.terminalRental);
    // Drafts saved before the sales-code picker existed have no such key.
    if (typeof r.salesCode === "string") setSalesCode(r.salesCode);
    // Drafts saved before this existed default to BUNDLED (matches the
    // state's own initial default).
    if (r.applicationPath) setApplicationPath(r.applicationPath);
    if (typeof r.authFeeOverride === "string")
      setAuthFeeOverride(r.authFeeOverride);
    if (typeof r.annualSecurityBundleFee === "string")
      setAnnualSecurityBundleFee(r.annualSecurityBundleFee);
    // Drafts saved before manual fee overrides existed have no such key;
    // spread over EMPTY_FEE_OVERRIDES so a field added after the draft was
    // saved still has a defined (blank) value instead of undefined.
    if (r.feeOverrides)
      setFeeOverrides({ ...EMPTY_FEE_OVERRIDES, ...r.feeOverrides });
    if (r.icPlusPricingOverrides)
      setIcPlusPricingOverrides({
        ...EMPTY_IC_PLUS_PRICING_OVERRIDES,
        ...r.icPlusPricingOverrides,
      });
    // Same fallback for Clover Security + TransArmor overrides: drafts
    // saved before they existed have no such key.
    if (r.cloverSecurityAndTransarmor)
      setCloverSecurityAndTransarmor({
        ...EMPTY_CLOVER_SECURITY_AND_TRANSARMOR,
        ...r.cloverSecurityAndTransarmor,
      });
    // Same fallback for fleet overrides: drafts saved before they existed
    // have no such key.
    if (r.fleetOverrides)
      setFleetOverrides({ ...EMPTY_FLEET_OVERRIDES, ...r.fleetOverrides });
    // Drafts saved before internal notes existed have no such key.
    if (typeof r.internalNotes === "string") setInternalNotes(r.internalNotes);
    setRestorePrompt(null);
    // Drafts saved before registeringTech existed have no such key; without
    // the fallback the state goes undefined and the next
    // registeringTech.assignedTechEmail read throws.
    setRegisteringTech(r.registeringTech ?? EMPTY_REGISTERING_TECH);
  }

  function dismissRestore() {
    discardDraft();
    setRestorePrompt(null);
  }

  // Live totals for the two percentage-bucket groups. Recomputed every
  // render (cheap; the sum is a handful of Number() calls). Surfaced via
  // PercentageTotalIndicator so users see the off-by-N before submit,
  // sidestepping the CardConnect-400 round-trip that was historically
  // the #1 cause of failed boardings (per the audit log).
  const modeTotal =
    (Number(mode.eCommercePct) || 0) +
    (Number(mode.keyedPct) || 0) +
    (Number(mode.mailOrderPct) || 0) +
    (Number(mode.swipedPct) || 0);

  // Tier resolved from the volume entered on the operator-fills-form path
  // (`form`). Gates the Manual fee adjustment section there -- "once a
  // pricing tier is selected". The send-intake path has no volume yet (the
  // recipient supplies it later), so its section is shown unconditionally
  // with tier={null}.
  const tier = selectPricingTier(vol.averageMonthlyVolume);

  const setBizField = patch(setBiz);
  const setOwnerField = patch(setOwner);
  const setDepositField = patch(setDeposit);
  const setWithdrawalField = patch(setWithdrawal);
  const setModeField = patch(setMode);
  const setVolField = patch(setVol);
  const setFeeOverrideField = patch(setFeeOverrides);
  const setIcPlusPricingOverrideField = patch(setIcPlusPricingOverrides);
  const setCloverSecurityAndTransarmorField = patch(
    setCloverSecurityAndTransarmor,
  );
  const setFleetOverrideField = patch(setFleetOverrides);

  function updatePdi<K extends keyof PdiPrefsInput>(
    key: K,
    value: PdiPrefsInput[K],
  ) {
    setPdiPrefs((prev) => ({ ...prev, [key]: value }));
  }

  function loadSample() {
    setBiz(SAMPLE.business);
    setBizAddr(SAMPLE.address);
    setMailSame(true);
    setOwner(SAMPLE.owner);
    setOwnerAddrSame(true);
    setDeposit(SAMPLE.bank);
    setWithdrawalSame(true);
    setMode(SAMPLE.mode);
    setPdiPrefs({
      ...PDI_DEFAULTS,
      posPlatform: "Gilbarco Passport",
      gasPumpYn: "Yes",
      gasPumpType: "Gilbarco Encore 700",
      singleOrMultiSite: "Single",
    });
    setError(null);
    setPreviewJson(null);
  }

  function setMnspFormHandler(next: MnspEnrollmentFormState) {
    setMnspForm(next);
    if (mnspError) setMnspError(null);
  }

  // Shared merchantData assembly for BOTH submit paths of the single form:
  // the operator-fills-and-submits path and the send-intake-link path. Same
  // underlying state (biz, bizAddr, owner, vol, etc.), so the two can never
  // drift out of sync on wire shape. `basics` is still parameterized because
  // the two paths trim/normalize businessName/contactEmail/contactPhone
  // slightly differently (the send path lower-cases + drops a blank phone at
  // the call site), even though both now read them from `biz`. MNSP
  // enrollment is layered on separately by buildInput() -- the send-intake
  // path bundles MNSP through a different mechanism entirely
  // (SendIntakeLinkInput.mnspBundle, a signing link, not inline capture here).
  function buildMerchantData(basics: {
    businessName: string;
    contactEmail: string;
    contactPhone?: string;
  }): MerchantDataInput {
    return {
      businessName: basics.businessName,
      contactEmail: basics.contactEmail,
      contactPhone: basics.contactPhone,
      dbaName: biz.dbaName.trim() || undefined,
      taxFilingName: biz.taxFilingName.trim() || undefined,
      taxFilingMethod: biz.taxFilingMethod || undefined,
      taxID: biz.taxID.trim() || undefined,
      mcc: biz.mcc.trim() || undefined,
      storeType: biz.storeType.trim() || undefined,
      businessDescription: biz.businessDescription.trim() || undefined,
      businessStartDate: biz.businessStartDate.trim() || undefined,
      website: biz.website.trim() || undefined,
      businessAddress: trimAddress(bizAddr),
      mailingAddress: mailSame ? trimAddress(bizAddr) : trimAddress(mailAddr),
      owner: {
        firstName: owner.firstName.trim() || undefined,
        lastName: owner.lastName.trim() || undefined,
        title: owner.title.trim() || "OWNER",
        email: owner.email.trim().toLowerCase() || undefined,
        phone: owner.phone.trim() || undefined,
        ownershipTypeCd: owner.ownershipTypeCd || undefined,
        ownerMobilePhone: owner.phone.trim(),
        ssn: owner.ssn.trim() || undefined,
        dateOfBirth: owner.dateOfBirth.trim() || undefined,
        ownershipPct: toIntOrUndef(owner.ownershipPct),
        driversLicenseNumber: owner.driversLicenseNumber.trim() || undefined,
        driversLicenseStateCd: owner.driversLicenseStateCd.trim() || undefined,
        address: ownerAddrSame ? trimAddress(bizAddr) : trimAddress(ownerAddr),
      },
      volumeDetails: {
        averageMonthlyVolume: toIntOrUndef(vol.averageMonthlyVolume),
        highTicketAmount: toIntOrUndef(vol.highTicketAmount),
        averageTicketAmount: toIntOrUndef(vol.averageTicketAmount),
      },
      deliveryPercentages: {
        dlvrySameDayPct: 100,
        dlvry0To7DaysPct: toIntOrUndef(deliveryPercentages.dlvry0To7DaysPct),
        dlvry8To14DaysPct: toIntOrUndef(deliveryPercentages.dlvry8To14DaysPct),
        dlvry15To30DaysPct: toIntOrUndef(
          deliveryPercentages.dlvry15To30DaysPct,
        ),
        dlvryOver30DaysPct: toIntOrUndef(
          deliveryPercentages.dlvryOver30DaysPct,
        ),
      },
      bankDetail: {
        deposit: {
          accountNumber: deposit.accountNumber.trim() || undefined,
          routingNumber: deposit.routingNumber.replace(/\D/g, "") || undefined,
          accountType: deposit.accountType || undefined,
          bankName: deposit.bankName.trim() || undefined,
        },
        withdrawal: withdrawalSame
          ? {
              accountNumber: deposit.accountNumber.trim() || undefined,
              routingNumber:
                deposit.routingNumber.replace(/\D/g, "") || undefined,
              accountType: deposit.accountType || undefined,
              bankName: deposit.bankName.trim() || undefined,
            }
          : {
              accountNumber: withdrawal.accountNumber.trim() || undefined,
              routingNumber:
                withdrawal.routingNumber.replace(/\D/g, "") || undefined,
              accountType: withdrawal.accountType || undefined,
              bankName: withdrawal.bankName.trim() || undefined,
            },
      },
      modeOfTransaction: {
        eCommercePct: toIntOrUndef(mode.eCommercePct),
        keyedPct: toIntOrUndef(mode.keyedPct),
        mailOrderPct: toIntOrUndef(mode.mailOrderPct),
        swipedPct: toIntOrUndef(mode.swipedPct),
      },
    };
  }

  function buildInput(): CreateApplicationInput | null {
    // Forced true whenever applicationPath is MNSP_ENROLLMENT_ONLY, in
    // case mnspEnrollment was unchecked before the operator switched paths
    // (the checkbox displays checked+disabled in that case, but its own
    // state var doesn't auto-flip).
    const effectiveRequireMNSP =
      mnspEnrollment || applicationPath === "MNSP_ENROLLMENT_ONLY";
    // Terminal rental is independent of MNSP -- not forced by
    // MNSP_ENROLLMENT_ONLY (that path is specifically about MNSP network
    // enrollment; rental stays whatever the operator separately chose).
    const effectiveRequireTerminalRental = terminalRental;
    let merchantData: MerchantDataInput = buildMerchantData({
      businessName: biz.businessName.trim(),
      contactEmail: biz.contactEmail.trim().toLowerCase(),
      contactPhone: biz.contactPhone.trim() || undefined,
    });

    if (effectiveRequireMNSP || effectiveRequireTerminalRental) {
      merchantData = {
        ...merchantData,
        mnspEnrollment: toWireMnsp(
          mnspForm,
          effectiveRequireMNSP,
          effectiveRequireTerminalRental,
        ),
      };
    }

    return {
      // Paired with the selected sales code; DEFAULT_TEMPLATE_ID when Auto.
      templateID: derivedTemplateId,
      applicationPath,
      // "" (Auto) is dropped so the backend applies the #64 volume threshold.
      salesCode: salesCode || undefined,
      // Operator auth-fee override. Blank or invalid -> omitted (pure tiered);
      // invalid is also blocked by localValidate before we reach here.
      authFeeOverride: authFeeOverrideInvalid(authFeeOverride)
        ? undefined
        : parseAuthFeeOverride(authFeeOverride),
      // Annual security bundle fee. Blank/invalid -> omitted (the $199 tier
      // default applies at display time); invalid is also blocked by
      // localValidate. Persisted to our DB only, never sent to CardConnect.
      annualSecurityBundleFee: annualSecurityBundleFeeInvalid(
        annualSecurityBundleFee,
      )
        ? undefined
        : parseAnnualSecurityBundleFee(annualSecurityBundleFee),
      // Admin-only manual overrides for every other fee. Blank/invalid
      // fields are dropped by buildFeeOverridesInput; invalid values are
      // also blocked by localValidate before we reach here.
      feeOverrides: buildFeeOverridesInput(feeOverrides),
      // Admin-only manual overrides for the icPlusPricing block. Same
      // blank/invalid-drop + localValidate-blocks-invalid contract as
      // feeOverrides above.
      icPlusPricingOverrides: buildIcPlusPricingOverridesInput(
        icPlusPricingOverrides,
      ),
      // Admin-only Clover Security + TransArmor overrides. Same
      // blank/invalid-drop + localValidate-blocks-invalid contract as
      // feeOverrides above. Unlike fleetOverrides below, this DOES reach
      // the CardConnect payload.
      cloverSecurityAndTransarmor: buildCloverSecurityAndTransarmorInput(
        cloverSecurityAndTransarmor,
      ),
      // Admin-only fleet-pricing overrides. Blank/invalid fields dropped by
      // buildFleetOverridesInput; persisted for future MAR/display only,
      // never sent to CardConnect.
      fleetOverrides: buildFleetOverridesInput(fleetOverrides),
      // Operator-only free-text notes. Never sent to CardConnect or
      // surfaced to the merchant.
      internalNotes: internalNotes.trim() || undefined,
      merchantData,
      // The PDI section is submitted on every path now -- on MNSP-enrollment-
      // only the managed-network order is opt-in via pdiPrefs.sendPdiOrder.
      pdiPrefs: {
        ...pdiPrefs,
        // Shipping speed is sourced from the MNSP selection (single
        // source of truth); the PDI section no longer has its own
        // dropdown.
        shippingSpeed: mnspShippingToPdiLabel(mnspForm.shippingSpeed),
        posPlatform: pdiPrefs.posPlatform?.trim() || "",
        gasPumpType: pdiPrefs.gasPumpType?.trim() || "",
        managedWifiInterest: pdiPrefs.managedWifiInterest?.trim() || "",
        notes: pdiPrefs.notes?.trim() || "",
      },
      assignedTechEmail:
        registeringTech.assignedTechEmail.trim().toLowerCase() || undefined,
      registeringFirstName:
        registeringTech.registeringFirstName.trim() || undefined,
      registeringLastName:
        registeringTech.registeringLastName.trim() || undefined,
      assignedToPIN: registeringTech.assignedToPIN.trim() || undefined,
      registeringPhone: registeringTech.registeringPhone.trim() || undefined,
      mnspBundle: effectiveRequireMNSP || undefined,
      terminalRentalBundle: effectiveRequireTerminalRental || undefined,
    };
  }

  // Per-field validation. Returns an array of {field, message} in
  // form-declaration order so the inline messages line up with where
  // the eye is on the page; the roll-up banner shows the same order.
  //
  // Field IDs match the input element id="..." in the form below so
  // the scroll-to-first-error + focus flow works. When adding a new
  // validation rule here, add the corresponding input id + render the
  // error string via fieldError("matching-id") in the form section
  // below, otherwise the error will only show in the roll-up banner.
  function localValidate(): FieldError[] {
    return validateCreateApplication({
      applicationPath,
      assignedTechEmail: registeringTech.assignedTechEmail,
      assignedToPIN: registeringTech.assignedToPIN,
      biz,
      bizAddr,
      mailAddr,
      mailSame,
      owner,
      ownerAddr,
      ownerAddrSame,
      deposit,
      withdrawal,
      withdrawalSame,
      mode,
      vol,
      authFeeOverride,
      annualSecurityBundleFee,
      feeOverrides,
      icPlusPricingOverrides,
      cloverSecurityAndTransarmor,
      fleetOverrides,
      mnspForm,
      mnspEnrollment,
      terminalRental,
    });
  }

  function sendIntakeFieldErrors(): FieldError[] {
    return validateSendIntake({
      applicationPath,
      assignedTechEmail: registeringTech.assignedTechEmail,
      assignedToPIN: registeringTech.assignedToPIN,
      biz,
      bizAddr,
      mailAddr,
      mailSame,
      owner,
      ownerAddr,
      ownerAddrSame,
      deposit,
      withdrawal,
      withdrawalSame,
      mode,
      vol,
      sendForm,
      authFeeOverride,
      annualSecurityBundleFee,
      feeOverrides,
      icPlusPricingOverrides,
      cloverSecurityAndTransarmor,
      fleetOverrides,
      mnspForm,
      mnspEnrollment,
      terminalRental,
    });
  }

  // The client-side field errors for whichever submit the form is currently
  // wired to -- the single source of truth for both the submit handlers and
  // the clear-on-fix effect.
  function activeClientFieldErrors(): FieldError[] {
    return sendMode === "send" ? sendIntakeFieldErrors() : localValidate();
  }

  // Recomputed every render, so a field's message tracks the value as the
  // operator types instead of being frozen at the last submit. useFieldErrors
  // decides which of these are allowed on screen yet (blurred, or any submit
  // attempted) and keeps server rejections on top.
  const clientErrors = activeClientFieldErrors();
  const {
    fieldError,
    visibleErrors,
    onBlurCapture,
    beginSubmit,
    setServerErrors,
    resetErrorDisplay,
  } = useFieldErrors(clientErrors);

  // Scroll to + focus whichever error sits highest on the page, not
  // whichever happens to be first in `errs` -- errs is assembled from several
  // independent checks (owner, banking, pricing overrides, …) in roughly but
  // not exactly form-declaration order, and the operator's eye should land on
  // the topmost problem regardless of validation order. Used by both the
  // client-side validation path (handleSubmit/handleSendIntake) and the
  // server-side path (when CARDCONNECT_ERROR comes back with fieldErrors).
  // Centralized so every rejection feels identical to the operator.
  function focusFirstError(errs: readonly FieldError[]) {
    setTimeout(() => {
      let topmost: HTMLElement | null = null;
      let topmostY = Infinity;
      for (const e of errs) {
        const el = document.getElementById(e.field);
        if (!el) continue;
        const top = el.getBoundingClientRect().top;
        if (top < topmostY) {
          topmost = el;
          topmostY = top;
        }
      }
      if (topmost) {
        topmost.scrollIntoView({ behavior: "smooth", block: "center" });
        topmost.focus?.();
      } else if (errorBannerRef.current) {
        errorBannerRef.current.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }
    }, 0);
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    // Clear a stale send-intake error left over from the other delivery mode.
    setSendError(null);
    setPreviewJson(null);

    const errs = beginSubmit();
    if (errs.length > 0) {
      focusFirstError(errs);
      return;
    }

    const input = buildInput();

    if (!input) {
      return;
    }

    // Preview/demo mode: never hit AppSync. Render the assembled payload so
    // the reviewer can see exactly what would be submitted.
    if (preview) {
      setPreviewJson(JSON.stringify(input, null, 2));
      window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" });
      return;
    }

    setLoading(true);
    try {
      const data = await gql<{ createApplication: ApplicationResult }>(
        CREATE_APPLICATION_MUTATION,
        { input },
      );
      const result = data.createApplication;
      // Navigate away from the create form whenever an application row was
      // created — on success, and also when boarding failed AFTER the row was
      // persisted (the server returns that ERROR row as `application`). Staying
      // on the form after a failed boarding let the operator re-submit and
      // mint a duplicate application (Susan, 2026-08-04); the detail page shows
      // the error and lets them edit + retry the SAME application instead.
      if (result.application?.applicationID) {
        // The draft has served its purpose (a row now exists). Drop it so a
        // future visit doesn't offer to restore data already turned into a row.
        discardDraft();
        const createdApplicationId = result.application.applicationID;
        await finishWithDocuments(createdApplicationId, () =>
          navigate(`/applications/${createdApplicationId}`, {
            replace: true,
            state:
              result.status === "success"
                ? undefined
                : { boardingError: result.message || null },
          }),
        );
        return;
      }
      // Server-side rejection: pipe field-level errors through the same
      // FieldError flow that powers client-side validation so each error
      // renders inline alongside its input + we scroll to the first one.
      const serverFieldErrors = (result.fieldErrors || []).map((f) => ({
        field: f.field,
        message: f.message,
      }));
      if (serverFieldErrors.length > 0) {
        setServerErrors(serverFieldErrors);
        focusFirstError(serverFieldErrors);
      }
      setError(
        result.message
          ? `${result.message}${
              result.errorCode ? ` (${result.errorCode})` : ""
            }`
          : "Application creation failed. Please try again.",
      );
    } catch (err: unknown) {
      if (err instanceof GraphQLError) {
        setError(err.message);
      } else {
        setError(
          err instanceof Error
            ? err.message
            : "Application creation failed. Please try again.",
        );
      }
    } finally {
      setLoading(false);
    }
  }

  // One submit handler for the single consolidated form: routes to the
  // create-now path or the send-intake-link path based on the operator's
  // delivery choice. Both underlying handlers call e.preventDefault().
  // Upload any attached documents to the just-created application, then run
  // `go` (the navigation the caller would otherwise have done directly). On a
  // failed upload, stay on the form with Retry / Continue instead.
  async function finishWithDocuments(applicationID: string, go: () => void) {
    const failed = await uploadApplicationDocuments(applicationID, documents);
    if (failed.length === 0) {
      go();
      return;
    }
    setDocumentUploadFailure({ applicationID, failed, go });
  }

  async function retryDocumentUploads() {
    if (!documentUploadFailure) return;
    const { applicationID, failed, go } = documentUploadFailure;
    const retry: PendingDocuments = {};
    for (const kind of failed) retry[kind] = documents[kind];
    setRetryingDocuments(true);
    try {
      const stillFailed = await uploadApplicationDocuments(
        applicationID,
        retry,
      );
      if (stillFailed.length === 0) {
        setDocumentUploadFailure(null);
        go();
      } else {
        setDocumentUploadFailure({ applicationID, failed: stillFailed, go });
      }
    } finally {
      setRetryingDocuments(false);
    }
  }

  // Attach a document (NewApplication's DocumentUploadRow "upload"): checks it
  // and holds it for finishWithDocuments. Throwing shows the row's error.
  async function attachDocument(kind: DocumentKind, file: File) {
    const problem = documentFileProblem(file);
    if (problem) throw new Error(problem);
    setDocuments((prev) => ({ ...prev, [kind]: file }));
  }

  function handleCreate(e: FormEvent) {
    // The application already exists (only a document upload failed); the
    // callout's Retry / Continue are the way forward, not a second submit.
    if (documentUploadFailure) {
      e.preventDefault();
      return;
    }
    return sendMode === "send" ? handleSendIntake(e) : handleSubmit(e);
  }

  // The two modes validate against different rule sets, so carrying a
  // submit attempt across the switch would paint the other mode's form red
  // before the operator has touched it.
  function switchSendMode(next: "fill" | "send") {
    if (next === sendMode) return;
    setSendMode(next);
    resetErrorDisplay();
  }

  // "Save as draft": the only thing a draft strictly needs is a coherent
  // identity (business name + contact email + the salesperson PIN/email the
  // backend requires on every create path) and, since we're persisting them,
  // no malformed pricing overrides. Owner title/type still have to pair if
  // both are filled. Everything else -- banking, address, ownership, MCC,
  // volume -- is optional and gets filled in later (by the operator via the
  // edit modal, or by the merchant via an intake link from the detail page).
  // Rule set lives in applicationValidation.ts (validateSaveDraft) so it's
  // unit-testable the same way validateCreateApplication/validateSendIntake
  // are.
  function draftValidate(): FieldError[] {
    return validateSaveDraft({
      applicationPath,
      assignedTechEmail: registeringTech.assignedTechEmail,
      assignedToPIN: registeringTech.assignedToPIN,
      biz,
      bizAddr,
      owner,
      vol,
      authFeeOverride,
      annualSecurityBundleFee,
      feeOverrides,
      icPlusPricingOverrides,
      cloverSecurityAndTransarmor,
      fleetOverrides,
      mnspForm,
      mnspEnrollment,
      terminalRental,
      // A send-mode draft is held to the send-intake bar (see validateSaveDraft).
      intakeRecipientType: sendMode === "send" ? sendForm.recipientType : null,
      intakeRecipientEmail: sendForm.recipientEmail,
    });
  }

  // The SendIntakeLinkInput the operator has set up in "send" mode. Shared by
  // handleSendIntake (dispatched immediately) and handleSaveDraft (staged on
  // the draft row -- the detail page's "Send intake link" button emails it
  // later via resendIntakeLink's no-args path). Mirrors the merchant-data
  // assembly buildInput()/handleSendIntake use for the terminal-rental fee.
  function buildSendIntakeInput(): SendIntakeLinkInput {
    const effectiveMnspBundle =
      mnspEnrollment || applicationPath === "MNSP_ENROLLMENT_ONLY";
    const effectiveTerminalRentalBundle = terminalRental;
    // autoSubmit is forced false for MNSP_ENROLLMENT_ONLY: that path never
    // reaches CardConnect, so there is nothing to auto-submit.
    const effectiveAutoSubmit =
      sendForm.autoSubmit && applicationPath !== "MNSP_ENROLLMENT_ONLY";
    const wireMnsp =
      effectiveMnspBundle || effectiveTerminalRentalBundle
        ? toWireMnsp(
            mnspForm,
            effectiveMnspBundle,
            effectiveTerminalRentalBundle,
          )
        : undefined;
    return {
      recipientType: sendForm.recipientType,
      recipientEmail: sendForm.recipientEmail.trim().toLowerCase(),
      ccEmail: sendForm.ccEmail.trim().toLowerCase() || undefined,
      expiresInDays: parseInt(sendForm.expiresInDays, 10) || 30,
      personalMessage: sendForm.personalMessage.trim() || undefined,
      autoSubmit: effectiveAutoSubmit,
      mnspBundle: effectiveMnspBundle,
      terminalRentalBundle: effectiveTerminalRentalBundle,
      applicationPath,
      // Only send a positive rental, and only when terminal rental is
      // requested (#86) -- independent of MNSP bundling.
      terminalMonthlyRentalFee:
        effectiveTerminalRentalBundle &&
        wireMnsp?.terminalMonthlyRentalFee &&
        wireMnsp.terminalMonthlyRentalFee > 0
          ? wireMnsp.terminalMonthlyRentalFee
          : undefined,
      gatewayName: wireMnsp?.gatewayName || undefined,
      // Whatever the operator pre-filled is locked on the merchant's intake
      // form (lib/intakeLocks.ts); the intake API enforces it.
      lockedFields: operatorEnteredPaths({
        biz,
        bizAddr,
        mailAddr,
        mailSame,
        owner,
        ownerAddr,
        ownerAddrSame,
        vol,
        deposit,
        withdrawal,
        withdrawalSame,
        mode,
      }),
    };
  }

  async function handleSaveDraft() {
    if (documentUploadFailure) return;
    setError(null);
    setSendError(null);
    setPreviewJson(null);

    // Drafts validate against draftValidate()'s narrow rule set, not the
    // whole form, so the results go straight through setServerErrors -- which
    // renders them inline and in the roll-up banner -- rather than
    // beginSubmit(), which would reveal every untouched field as an error.
    const errs = draftValidate();
    if (errs.length > 0) {
      setServerErrors(errs);
      focusFirstError(errs);
      return;
    }
    setServerErrors([]);

    const input = buildInput();
    if (!input) return;

    // In "send" mode the draft carries the intake setup so it can be emailed
    // later without re-entering the recipient. No token/email is generated
    // now -- the backend stages IntakeRecipientType/Email + AutoSubmit and
    // leaves the row at CREATED for the detail-page "Send intake link" action.
    const intake = sendMode === "send" ? buildSendIntakeInput() : null;

    setSavingDraft(true);
    try {
      const data = await gql<{ saveDraftApplication: ApplicationResult }>(
        SAVE_DRAFT_APPLICATION_MUTATION,
        { input, intake },
      );
      const result = data.saveDraftApplication;
      if (result.application?.applicationID) {
        discardDraft();
        const createdApplicationId = result.application.applicationID;
        await finishWithDocuments(createdApplicationId, () =>
          navigate(`/applications/${createdApplicationId}`, {
            replace: true,
          }),
        );
        return;
      }
      const serverFieldErrors = (result.fieldErrors || []).map((f) => ({
        field: f.field,
        message: f.message,
      }));
      if (serverFieldErrors.length > 0) {
        setServerErrors(serverFieldErrors);
        focusFirstError(serverFieldErrors);
      }
      setError(
        result.message
          ? `${result.message}${
              result.errorCode ? ` (${result.errorCode})` : ""
            }`
          : "Could not save the draft. Please try again.",
      );
    } catch (err: unknown) {
      setError(
        err instanceof GraphQLError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Could not save the draft. Please try again.",
      );
    } finally {
      setSavingDraft(false);
    }
  }

  const merchantRequired = sendMode === "fill";
  // "Internal PP Sales/Admin": sales starts the application and the link goes
  // to the merchant to finish it, so only the merchant's email is required.
  const internalSales =
    sendMode === "send" && sendForm.recipientType === "INTERNAL_SALES";
  // Business name + merchant contact email: required when filling the whole
  // application, and on an intake to address the invitation -- except an
  // internal-sales intake, where the merchant email (the recipient) is enough.
  const identityRequired = merchantRequired || !internalSales;

  // Auto-submit-to-CardConnect toggle. Only meaningful on the send-intake
  // path (there's nothing to auto-submit when the operator submits directly),
  // and never for MNSP_ENROLLMENT_ONLY (that path never reaches CardConnect).
  const autoSubmitBlock = (
    <div>
      <label htmlFor="send-autoSubmit" className="choice-tile">
        <input
          id="send-autoSubmit"
          type="checkbox"
          checked={
            sendForm.autoSubmit && applicationPath !== "MNSP_ENROLLMENT_ONLY"
          }
          disabled={applicationPath === "MNSP_ENROLLMENT_ONLY"}
          onChange={(e) =>
            setSendForm((p) => ({ ...p, autoSubmit: e.target.checked }))
          }
          className="mt-0.5"
        />
        <span className="flex-1">
          <span className="block font-medium">
            Auto-submit to CardConnect on completion
          </span>
          <span className="mt-0.5 block text-xs text-brand-muted">
            {applicationPath === "MNSP_ENROLLMENT_ONLY"
              ? "Not applicable — MNSP Enrollment Only applications are never submitted to CardConnect."
              : "Skips operator review. The completed form goes straight to CardConnect and the signature-request email is sent. Use when you trust the recipient (e.g. a tech filling on behalf of a long-time customer)."}
          </span>
        </span>
      </label>
    </div>
  );

  const RECIPIENT_OPTIONS: {
    value: IntakeRecipientType;
    title: string;
    detail: string;
  }[] = [
    {
      value: "MERCHANT",
      title: "Merchant",
      detail: "They fill it out themselves.",
    },
    {
      value: "TECH",
      title: "Tech",
      detail: "Filling in on behalf of the merchant.",
    },
    {
      value: "BOTH",
      title: "Both",
      detail: "Link goes to both; either can fill it in.",
    },
    {
      value: "INTERNAL_SALES",
      title: "Internal PP Sales/Admin",
      detail:
        "Fill in what you know; the merchant gets the link and finishes the rest.",
    },
  ];

  const form = (
    <form
      onSubmit={handleCreate}
      onBlurCapture={onBlurCapture}
      autoComplete="off"
      noValidate
      className="card max-w-5xl space-y-7 p-6 sm:p-8"
    >
      {!preview && (
        <Section
          title="How do you want to do this?"
          description="Choose who completes the application and which products it covers."
        >
          <div className="grid gap-5 lg:grid-cols-2">
            <div>
              <p className="label mb-1.5 block">Delivery</p>
              <div
                role="group"
                aria-label="Application creation mode"
                className="seg"
              >
                <button
                  type="button"
                  aria-pressed={sendMode === "fill"}
                  onClick={() => switchSendMode("fill")}
                  className="seg-item"
                >
                  Fill out &amp; submit now
                </button>
                <button
                  type="button"
                  aria-pressed={sendMode === "send"}
                  onClick={() => switchSendMode("send")}
                  className="seg-item"
                >
                  Email an intake link
                </button>
              </div>
              <p className="hint">
                {sendMode === "send"
                  ? "A merchant or tech completes the application from a tokenized link."
                  : "You complete everything here and it boards to CardConnect on submit."}
              </p>
            </div>
          </div>
          <div>
            <p id="application-path-label" className="label mb-1.5 block">
              Application path
            </p>
            <div
              role="radiogroup"
              aria-labelledby="application-path-label"
              className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4"
            >
              {APPLICATION_OPTIONS.map((opt) => (
                <label key={opt.value} className="choice-tile">
                  <input
                    type="radio"
                    name="application-path"
                    value={opt.value}
                    checked={applicationOption === opt.value}
                    onChange={() => selectApplicationOption(opt.value)}
                    className="mt-0.5"
                  />
                  <span className="flex-1">
                    <span className="block font-medium">{opt.title}</span>
                    <span className="mt-0.5 block text-xs text-brand-muted">
                      {opt.detail}
                    </span>
                  </span>
                </label>
              ))}
            </div>
          </div>
          {sendMode === "send" && autoSubmitBlock}
          {sendMode === "send" && (
            <div className="panel space-y-5 p-4 sm:p-5">
              <p className="form-subsection-title">Intake link options</p>
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <div className="max-w-[11.5rem]">
                  <label htmlFor="send-expires" className="label mb-1.5 block">
                    Link expires after (days)
                  </label>
                  {(() => {
                    const n = parseInt(sendForm.expiresInDays, 10);
                    const invalid = !Number.isFinite(n) || n < 1 || n > 90;
                    return (
                      <>
                        <input
                          id="send-expires"
                          type="text"
                          inputMode="numeric"
                          value={sendForm.expiresInDays}
                          onChange={(e) =>
                            setSendForm((p) => ({
                              ...p,
                              expiresInDays: e.target.value.replace(/\D/g, ""),
                            }))
                          }
                          maxLength={2}
                          className="input"
                          aria-invalid={invalid || undefined}
                          aria-describedby="send-expires-hint"
                        />
                        {invalid ? (
                          <FieldErrorText id="send-expires-hint">
                            Must be between 1 and 90 days.
                          </FieldErrorText>
                        ) : (
                          <p id="send-expires-hint" className="hint">
                            1–90 days. Default 30.
                          </p>
                        )}
                      </>
                    );
                  })()}
                </div>
              </div>
              <div>
                <label htmlFor="send-message" className="label mb-1.5 block">
                  Personal message{" "}
                  <span className="font-normal text-brand-muted">
                    (optional)
                  </span>
                </label>
                <textarea
                  id="send-message"
                  className="input min-h-[88px] resize-y"
                  value={sendForm.personalMessage}
                  onChange={(e) =>
                    setSendForm((p) => ({
                      ...p,
                      personalMessage: e.target.value,
                    }))
                  }
                  maxLength={500}
                  placeholder="Included in the invitation email."
                />
              </div>
            </div>
          )}
        </Section>
      )}

      {sendMode === "send" && (
        <Section title="Who should fill it out?">
          <div
            role="radiogroup"
            aria-label="Intake recipient"
            className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4"
          >
            {RECIPIENT_OPTIONS.map((opt) => (
              <label key={opt.value} className="choice-tile">
                <input
                  type="radio"
                  name="send-recipientType"
                  value={opt.value}
                  checked={sendForm.recipientType === opt.value}
                  onChange={() =>
                    setSendForm((p) => ({ ...p, recipientType: opt.value }))
                  }
                  className="mt-0.5"
                />
                <span className="flex-1">
                  <span className="block font-medium">{opt.title}</span>
                  <span className="mt-0.5 block text-xs text-brand-muted">
                    {opt.detail}
                  </span>
                </span>
              </label>
            ))}
          </div>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <TF
              id="send-recipientEmail"
              label={internalSales ? "Merchant email" : "Recipient email"}
              required
              hint={
                internalSales
                  ? "The only required field. The merchant gets the link and finishes the application."
                  : "Who this intake link goes to."
              }
              type="email"
              value={sendForm.recipientEmail}
              onChange={(v) =>
                setSendForm((p) => ({ ...p, recipientEmail: v }))
              }
              maxLength={320}
              error={fieldError("send-recipientEmail")}
            />
            {sendForm.recipientType === "BOTH" && (
              <TF
                id="send-ccEmail"
                label="CC email (the second recipient)"
                required
                type="email"
                value={sendForm.ccEmail}
                onChange={(v) => setSendForm((p) => ({ ...p, ccEmail: v }))}
                maxLength={320}
                error={fieldError("send-ccEmail")}
              />
            )}
          </div>
        </Section>
      )}

      <Section title="Business">
        <TF
          id="businessName"
          label="Legal business name"
          required={identityRequired}
          value={biz.businessName}
          onChange={(v) => setBizField("businessName", v)}
          maxLength={200}
          placeholder="Acme C-Store LLC"
          hint="The legal name as it should appear in CardConnect."
          error={fieldError("businessName")}
        />
        <BusinessDetailsFields
          biz={biz}
          setBizField={setBizField}
          fieldError={fieldError}
          required={merchantRequired}
        />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <TF
            id="contactEmail"
            label="Merchant contact email"
            required={identityRequired}
            type="email"
            inputMode="email"
            value={biz.contactEmail}
            onChange={(v) => setBizField("contactEmail", v)}
            maxLength={320}
            placeholder="owner@acmecstore.com"
            hint={
              internalSales
                ? "Leave blank to use the merchant email above. Becomes their CardConnect login email."
                : "Becomes the merchant's CardConnect login email."
            }
            error={fieldError("contactEmail")}
          />
          <TF
            id="contactPhone"
            label="Business phone"
            value={biz.contactPhone}
            onChange={(v) => setBizField("contactPhone", v)}
            format="phone"
            required={merchantRequired}
            error={fieldError("contactPhone")}
          />
        </div>
        <VolumeFields
          vol={vol}
          setVolField={setVolField}
          authFeeOverride={authFeeOverride}
          feeOverrides={feeOverrides}
          fleetOverrides={fleetOverrides}
          cloverSecurityAndTransarmor={cloverSecurityAndTransarmor}
          icPlusPricingOverrides={icPlusPricingOverrides}
          salesCode={salesCode}
          annualSecurityBundleFee={annualSecurityBundleFee}
          fieldError={fieldError}
        />
      </Section>

      <Section title="Business address">
        <AddressFields
          prefix="biz"
          value={bizAddr}
          set={(k, v) => setBizAddr((p) => ({ ...p, [k]: v }))}
          errorAt={fieldError}
          required={merchantRequired}
        />
        <SameAsToggle
          checked={mailSame}
          onChange={setMailSame}
          label="Mailing address is the same as the business address"
          subtitle="Mailing address"
        >
          <AddressFields
            prefix="mail"
            value={mailAddr}
            set={(k, v) => setMailAddr((p) => ({ ...p, [k]: v }))}
            errorAt={fieldError}
            required={merchantRequired}
          />
        </SameAsToggle>
      </Section>

      <Section title="Ownership">
        <OwnershipFields
          owner={owner}
          setOwnerField={setOwnerField}
          ownerAddr={ownerAddr}
          setOwnerAddr={setOwnerAddr}
          ownerAddrSame={ownerAddrSame}
          setOwnerAddrSame={setOwnerAddrSame}
          fieldError={fieldError}
          required={merchantRequired}
        />
      </Section>

      <Section
        title="Banking"
        description="Account and routing numbers go to CardConnect for boarding; only the last 4 digits are kept on file."
      >
        <BankingFields
          deposit={deposit}
          setDepositField={setDepositField}
          withdrawal={withdrawal}
          setWithdrawalField={setWithdrawalField}
          withdrawalSame={withdrawalSame}
          setWithdrawalSame={setWithdrawalSame}
          fieldError={fieldError}
          required={merchantRequired}
        />
      </Section>

      <Section
        title="Documents"
        description="The merchant's driver's license and voided check, kept on file by Payment Products. Optional on every path; they upload when the application is saved or sent. A merchant who gets an intake link can add them there too."
      >
        <div className="space-y-3">
          {DOCUMENT_KINDS.map(({ kind, label }) => (
            <DocumentUploadRow
              key={kind}
              label={label}
              kind={kind}
              uploaded={Boolean(documents[kind])}
              onUpload={attachDocument}
              required={false}
              deferred
            />
          ))}
        </div>
      </Section>

      <Section
        title="Transaction mix"
        description="How card payments are accepted, as percentages. Must total 100."
        aside={
          <PercentageTotalIndicator
            id="modeOfTransaction-total"
            total={modeTotal}
          />
        }
      >
        <TransactionMixFields
          mode={mode}
          setModeField={setModeField}
          fieldError={fieldError}
          required={merchantRequired}
        />
      </Section>
      <Section
        title="Salesperson"
        description="The salesperson assigned to this merchant."
      >
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <TF
            id="assigned-tech-email"
            label="Salesperson's email"
            type="email"
            value={registeringTech.assignedTechEmail}
            onChange={(v) =>
              setRegisteringTech((p) => ({ ...p, assignedTechEmail: v }))
            }
            required={merchantRequired}
            maxLength={200}
            error={fieldError("assigned-tech-email")}
          />
          <TF
            id="assignedToPIN"
            label="Salesperson's PIN"
            type="text"
            value={registeringTech.assignedToPIN}
            onChange={(v) =>
              setRegisteringTech((p) => ({ ...p, assignedToPIN: v }))
            }
            maxLength={8}
            required={merchantRequired}
            error={fieldError("assignedToPIN")}
          />
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <TF
            id="assigned-tech-firstName"
            label="Salesperson's first name"
            type="text"
            value={registeringTech.registeringFirstName}
            onChange={(v) =>
              setRegisteringTech((p) => ({ ...p, registeringFirstName: v }))
            }
            maxLength={100}
          />
          <TF
            id="assigned-tech-LastName"
            label="Salesperson's last name"
            type="text"
            value={registeringTech.registeringLastName}
            onChange={(v) =>
              setRegisteringTech((p) => ({ ...p, registeringLastName: v }))
            }
            maxLength={100}
          />
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <TF
            id="assigned-tech-phone"
            label="Salesperson's phone"
            value={registeringTech.registeringPhone}
            onChange={(v) =>
              setRegisteringTech((p) => ({
                ...p,
                registeringPhone: v,
              }))
            }
            format="phone"
          />
        </div>
      </Section>

      <Section
        title="Internal notes"
        description="Admin only — pricing context, special arrangements, etc. Never shown to the merchant."
      >
        <div>
          <label htmlFor="internal-notes" className="label mb-1.5 block">
            Notes
          </label>
          <textarea
            id="internal-notes"
            className="input min-h-[88px] resize-y"
            value={internalNotes}
            onChange={(e) => setInternalNotes(e.target.value)}
            maxLength={2000}
            placeholder="e.g. MNSP reduced to $115/mo, deployment fee waived"
          />
        </div>
      </Section>

      <Section title="CardConnect portfolio">
        <SalesCodeSelect
          id="salesCode"
          value={salesCode}
          onChange={setSalesCode}
          includeAllPortfolios
        />
        <div className="panel flex flex-wrap items-baseline gap-x-2 px-4 py-3 text-sm text-brand-muted">
          <span>
            Template{" "}
            <code className="rounded bg-brand-surface-2 px-1.5 py-0.5 font-mono text-[13px] text-brand-text">
              {derivedTemplateId}
            </code>{" "}
            {salesCode
              ? "— paired with the selected sales code."
              : "— default (Auto)."}
          </span>
          <span className="text-brand-muted/80">
            Pricing, platform, and MID details come from the template.
          </span>
        </div>
      </Section>

      {(sendMode === "send" || tier) && (
        <Section
          title="Manual fee adjustment"
          description="Admin only. Leave a field blank to keep the tier default."
        >
          <ManualFeeAdjustmentSection
            idPrefix="fee-override"
            tier={tier}
            monthlyVolume={vol.averageMonthlyVolume}
            salesCode={salesCode}
            authFeeOverride={authFeeOverride}
            onAuthFeeOverrideChange={setAuthFeeOverride}
            authFeeOverrideError={fieldError("fee-override.authFee")}
            annualSecurityBundleFee={annualSecurityBundleFee}
            onAnnualSecurityBundleFeeChange={setAnnualSecurityBundleFee}
            annualSecurityBundleFeeError={fieldError(
              "fee-override.annualSecurityBundleFee",
            )}
            feeOverrides={feeOverrides}
            onFeeOverrideChange={setFeeOverrideField}
            errorAt={fieldError}
            icPlusPricingOverrides={icPlusPricingOverrides}
            onIcPlusPricingOverrideChange={setIcPlusPricingOverrideField}
          />
          <CloverSecurityAndTransarmorSection
            idPrefix="clover-security"
            tier={tier}
            cloverSecurityAndTransarmor={cloverSecurityAndTransarmor}
            onCloverSecurityAndTransarmorChange={
              setCloverSecurityAndTransarmorField
            }
            feeOverrides={feeOverrides}
            onFeeOverrideChange={setFeeOverrideField}
            feeIdPrefix="fee-override"
            errorAt={fieldError}
          />
          <FleetPricingAdjustmentSection
            idPrefix="fleet-override"
            tier={tier}
            fleetOverrides={fleetOverrides}
            onFleetOverrideChange={setFleetOverrideField}
            errorAt={fieldError}
          />
        </Section>
      )}

      {/* PDI section: collapsible; shown on every path. On MNSP-enrollment-only
          the managed-network order is opt-in (the toggle defaults to "No"). */}
      {
        <Section
          title="Equipment & network (PDI)"
          description="Always submitted with tier defaults; expand to override."
          aside={
            <button
              type="button"
              onClick={() => setPdiOpen((v) => !v)}
              className="inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium text-brand-slate transition-colors hover:bg-brand-border/40 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue-bright/40"
              aria-expanded={pdiOpen}
              aria-controls="pdi-section"
            >
              {pdiOpen ? "Hide" : "Show"}
              {pdiOpen ? (
                <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" />
              ) : (
                <ChevronRight className="h-3.5 w-3.5" aria-hidden="true" />
              )}
            </button>
          }
        >
          {pdiOpen ? (
            <div id="pdi-section" className="space-y-5">
              <PdiFields
                pdiPrefs={pdiPrefs}
                updatePdi={updatePdi}
                posOtherMode={posOtherMode}
                setPosOtherSelected={setPosOtherSelected}
                gasOtherSelected={gasOtherSelected}
                setGasOtherSelected={setGasOtherSelected}
                enrollmentOnly={applicationPath === "MNSP_ENROLLMENT_ONLY"}
              />
            </div>
          ) : (
            <div id="pdi-section" hidden />
          )}
        </Section>
      }

      {/* Nothing rides along with a CardConnect Only application. */}
      {applicationOption !== "CARDCONNECT_ONLY" && (
        <Section
          title="Products"
          description="The MAR and network agreements that ride along with this application. The application path above decides MNSP enrollment."
        >
          <label
            htmlFor="terminal-rental-requirement-check"
            className="choice-tile sm:max-w-md"
          >
            <input
              id="terminal-rental-requirement-check"
              type="checkbox"
              checked={terminalRental}
              disabled={!rentalIsOptional(applicationOption)}
              onChange={(e) => setTerminalRental(e.target.checked)}
              className="mt-0.5"
            />
            <span className="flex-1">
              <span className="block font-medium">Terminal rental</span>
              <span className="mt-0.5 block text-xs text-brand-muted">
                {rentalIsOptional(applicationOption)
                  ? "Monthly equipment fee, added to the MAR."
                  : "Included with CardConnect + MAR."}
              </span>
            </span>
          </label>

          {(mnspEnrollment ||
            terminalRental ||
            applicationPath === "MNSP_ENROLLMENT_ONLY") && (
            <div id="mnsp-enrollment-section-anchor">
              <MnspEnrollmentSection
                form={mnspForm}
                onChange={setMnspFormHandler}
                mnspEnabled={
                  mnspEnrollment || applicationPath === "MNSP_ENROLLMENT_ONLY"
                }
                rentalEnabled={terminalRental}
                error={mnspError}
                feeError={fieldError}
                noSignature
                registerRequired={merchantRequired}
              />
              {sendMode === "send" && (
                <p className="hint mt-3">
                  The recipient's intake form will include the matching
                  equipment and pricing fields plus per-section acceptance
                  checkboxes for the Merchant Acknowledgement Receipt and End
                  User Agreement. They cannot submit until every applicable
                  section is acknowledged.
                </p>
              )}
            </div>
          )}
        </Section>
      )}

      {documentUploadFailure && (
        <div role="alert" className="callout-danger space-y-3 px-4 py-3">
          <p>
            The application was saved, but the{" "}
            {documentUploadFailure.failed
              .map(documentLabel)
              .join(" and ")
              .toLowerCase()}{" "}
            couldn't be uploaded. Retry, or continue without{" "}
            {documentUploadFailure.failed.length === 1 ? "it" : "them"} (the
            merchant can still add them from an intake link).
          </p>
          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              onClick={() => void retryDocumentUploads()}
              className="btn-primary py-2"
              disabled={retryingDocuments}
            >
              {retryingDocuments ? "Retrying..." : "Retry upload"}
            </button>
            <button
              type="button"
              onClick={() => {
                const { go } = documentUploadFailure;
                setDocumentUploadFailure(null);
                go();
              }}
              className="btn-secondary py-2"
              disabled={retryingDocuments}
            >
              Continue without{" "}
              {documentUploadFailure.failed.length === 1 ? "it" : "them"}
            </button>
          </div>
        </div>
      )}

      {visibleErrors.length > 0 && (
        <div
          ref={errorBannerRef}
          role="alert"
          aria-live="assertive"
          className="callout-danger flex gap-3 px-4 py-3"
        >
          <AlertCircle
            className="mt-0.5 h-4 w-4 shrink-0 text-danger"
            aria-hidden="true"
          />
          <div className="min-w-0 flex-1">
            <p className="font-medium text-white">
              {visibleErrors.length}{" "}
              {visibleErrors.length === 1 ? "field needs" : "fields need"}{" "}
              attention before submitting
            </p>
            <ul className="mt-1.5 space-y-0.5">
              {visibleErrors.map((e) => (
                <li key={e.field}>
                  <button
                    type="button"
                    onClick={() => {
                      const el = document.getElementById(e.field);
                      if (el) {
                        el.scrollIntoView({
                          behavior: "smooth",
                          block: "center",
                        });
                        (el as HTMLElement).focus?.();
                      }
                    }}
                    className="text-left text-danger-soft underline-offset-2 hover:text-white hover:underline focus:outline-none focus:underline"
                  >
                    {e.message}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {error && <ErrorMessage message={error} />}
      {sendError && <ErrorMessage message={sendError} />}

      <div className="flex flex-col-reverse gap-3 border-t border-brand-border pt-6 sm:flex-row sm:items-center sm:justify-end">
        {!preview && (
          <Link
            to="/applications"
            className="btn-secondary py-2.5"
            aria-disabled={loading || sending || savingDraft}
          >
            Cancel
          </Link>
        )}
        {/* Save-as-draft: both paths. "fill" stages a resumable row to
            finish + submit later; "send" stages the intake setup (recipient,
            auto-submit, personal message) so the detail page's "Send intake
            link" button emails it when the operator is ready. Neither touches
            CardConnect and neither sends an email now. type="button" so the
            form's native `required` fields don't block it -- draftValidate()
            gates it instead. */}
        {!preview && (
          <button
            type="button"
            onClick={handleSaveDraft}
            disabled={loading || sending || savingDraft}
            className="btn-secondary py-2.5"
            title={
              sendMode === "send"
                ? "Stage this intake link to send later — the recipient and options are saved on the application, but no email goes out until you send it from the application page"
                : "Save this application to finish or send later — nothing is submitted to CardConnect and no intake link is sent"
            }
          >
            {savingDraft ? "Saving draft..." : "Save as draft"}
          </button>
        )}
        <button
          type="submit"
          disabled={loading || sending || savingDraft}
          className="btn-primary min-w-[12rem] py-2.5"
        >
          {preview
            ? "Preview payload"
            : sendMode === "send"
              ? sending
                ? "Sending..."
                : "Send intake link"
              : loading
                ? "Creating..."
                : "Create application"}
        </button>
      </div>

      {preview && previewJson && (
        <div className="panel mt-4 p-4">
          <p className="mb-2 text-sm font-medium text-brand-text">
            Assembled CreateApplicationInput (not sent in preview)
          </p>
          <p className="mb-3 text-xs text-brand-muted">
            In storage, these are reduced to a masked last-4 and never written
            raw: <code>merchantData.taxID</code>, <code>owner.ssn</code>,{" "}
            <code>bankDetail.*.accountNumber</code>,{" "}
            <code>bankDetail.*.routingNumber</code>.
          </p>
          <pre className="overflow-x-auto rounded-lg bg-brand-bg-deep p-3 text-xs text-brand-text">
            {previewJson}
          </pre>
        </div>
      )}
    </form>
  );

  // ----- Send-link delivery path (createAndSendIntake) -------------------
  // Runs when the single form is submitted with the "Email an intake link"
  // delivery option selected (sendMode === "send"): the operator emails a
  // tokenized intake link instead of completing + submitting the application
  // themselves. Goes through CREATE_AND_SEND_INTAKE_MUTATION and navigates to
  // the application detail page on success. Reads the same shared form state
  // (biz, owner, registeringTech, mnspEnrollment, …) as handleSubmit; sendForm
  // only carries the link-specific fields (recipient, expiry, message,
  // auto-submit).

  async function handleSendIntake(e: FormEvent) {
    e.preventDefault();
    setSendError(null);
    // Clear a stale create-path error left over from the other delivery mode.
    setError(null);

    // Same treatment as the operator-fills submit: every problem shows
    // inline, rolls up into the banner, and scrolls to the topmost one.
    const errs = beginSubmit();
    if (errs.length > 0) {
      focusFirstError(errs);
      return;
    }

    // Business + salesperson fields now come from the shared `biz` /
    // `registeringTech` state (same inputs the operator-fills path uses),
    // not a send-only copy.
    const trimmed = {
      businessName: biz.businessName.trim(),
      contactEmail: biz.contactEmail.trim().toLowerCase(),
      contactPhone: biz.contactPhone.trim(),
      recipientEmail: sendForm.recipientEmail.trim().toLowerCase(),
      ccEmail: sendForm.ccEmail.trim().toLowerCase(),
      personalMessage: sendForm.personalMessage.trim(),
      assignedToPIN: registeringTech.assignedToPIN.trim(),
      assignedTechEmail: registeringTech.assignedTechEmail.trim().toLowerCase(),
      registeringFirstName: registeringTech.registeringFirstName.trim(),
      registeringLastName: registeringTech.registeringLastName.trim(),
      registeringPhone: registeringTech.registeringPhone.trim(),
    };
    setSending(true);
    try {
      // Same assembly buildInput() uses for the operator-fills form, so
      // whatever the operator optionally filled in below ("Fill out some
      // of it") rides along; anything left blank resolves to undefined
      // and the recipient fills it in via the intake link.
      // Forced true whenever applicationPath is MNSP_ENROLLMENT_ONLY, in
      // case mnspEnrollment was unchecked before the operator switched
      // paths (the checkbox displays checked+disabled in that case, but
      // its own state var doesn't auto-flip) — see the "MNSP enrollment"
      // checkbox above. autoSubmit is forced false: this application never
      // reaches CardConnect, so there is nothing to auto-submit.
      // terminalRental is NOT forced by MNSP_ENROLLMENT_ONLY -- rental is
      // independent of that path.
      const effectiveMnspBundle =
        mnspEnrollment || applicationPath === "MNSP_ENROLLMENT_ONLY";
      const effectiveTerminalRentalBundle = terminalRental;
      let sendMerchantData: MerchantDataInput = buildMerchantData({
        businessName: trimmed.businessName,
        contactEmail: trimmed.contactEmail,
        contactPhone: trimmed.contactPhone || undefined,
      });
      if (effectiveMnspBundle || effectiveTerminalRentalBundle) {
        sendMerchantData = {
          ...sendMerchantData,
          mnspEnrollment: toWireMnsp(
            mnspForm,
            effectiveMnspBundle,
            effectiveTerminalRentalBundle,
          ),
        };
      }
      const application: CreateApplicationInput = {
        templateID: derivedTemplateId,
        applicationPath,
        salesCode: salesCode || undefined,
        // Operator auth-fee override. Persisted on the intake row and applied
        // when the recipient's completed application submits to CardConnect.
        authFeeOverride: parseAuthFeeOverride(authFeeOverride),
        // Annual security bundle fee. Persisted on the intake row; our records
        // only, never sent to CardConnect. Blank/invalid -> omitted ($199
        // default applies at display time).
        annualSecurityBundleFee: annualSecurityBundleFeeInvalid(
          annualSecurityBundleFee,
        )
          ? undefined
          : parseAnnualSecurityBundleFee(annualSecurityBundleFee),
        // Admin-only manual overrides for every other fee. Persisted on the
        // intake row the same way, applied when the recipient's completed
        // application submits.
        feeOverrides: buildFeeOverridesInput(feeOverrides),
        // Admin-only manual overrides for the icPlusPricing block. Same
        // persist-on-intake-row-and-apply-later contract as feeOverrides.
        icPlusPricingOverrides: buildIcPlusPricingOverridesInput(
          icPlusPricingOverrides,
        ),
        // Admin-only Clover Security + TransArmor overrides. Same
        // persist-on-intake-row-and-apply-later contract as feeOverrides --
        // unlike fleetOverrides below, this DOES reach the CardConnect
        // payload.
        cloverSecurityAndTransarmor: buildCloverSecurityAndTransarmorInput(
          cloverSecurityAndTransarmor,
        ),
        // Admin-only fleet-pricing overrides. Persisted on the intake row for
        // future MAR/display; never sent to CardConnect.
        fleetOverrides: buildFleetOverridesInput(fleetOverrides),
        // Operator-only free-text notes. Never sent to CardConnect or
        // surfaced to the merchant/intake form.
        internalNotes: internalNotes.trim() || undefined,
        merchantData: sendMerchantData,
        // The PDI section is submitted on every path now -- on MNSP-enrollment-
        // only the managed-network order is opt-in via pdiPrefs.sendPdiOrder.
        pdiPrefs: {
          ...pdiPrefs,
          // Shipping speed is sourced from the MNSP selection (single
          // source of truth), same as the operator-fills form.
          shippingSpeed: mnspShippingToPdiLabel(mnspForm.shippingSpeed),
          posPlatform: pdiPrefs.posPlatform?.trim() || "",
          gasPumpType: pdiPrefs.gasPumpType?.trim() || "",
          managedWifiInterest: pdiPrefs.managedWifiInterest?.trim() || "",
          notes: pdiPrefs.notes?.trim() || "",
        },
        assignedToPIN: trimmed.assignedToPIN || undefined,
        assignedTechEmail: trimmed.assignedTechEmail,
        registeringFirstName: trimmed.registeringFirstName,
        registeringLastName: trimmed.registeringLastName,
        registeringPhone: trimmed.registeringPhone,
      };
      const intake: SendIntakeLinkInput = buildSendIntakeInput();
      const data = await gql<{ createAndSendIntake: ApplicationResult }>(
        CREATE_AND_SEND_INTAKE_MUTATION,
        { application, intake },
      );
      const result = data.createAndSendIntake;
      if (result.status === "success" && result.application?.applicationID) {
        discardDraft();
        const createdApplicationId = result.application.applicationID;
        await finishWithDocuments(createdApplicationId, () =>
          navigate(`/applications/${createdApplicationId}`, {
            replace: true,
          }),
        );
        return;
      }
      setSendError(
        result.message
          ? `${result.message}${
              result.errorCode ? ` (${result.errorCode})` : ""
            }`
          : "Could not send the intake link. Try again.",
      );
    } catch (err: unknown) {
      setSendError(
        err instanceof GraphQLError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Could not send the intake link. Try again.",
      );
    } finally {
      setSending(false);
    }
  }

  // Dev-only "Load sample data" affordance. Seeds the shared merchant state
  // (biz / owner / bank / …) that both delivery modes now use, from the same
  // SAMPLE constant the preview/demo button uses. import.meta.env.DEV is
  // stripped to false in `pnpm build`, so this button doesn't ship to prod
  // even though the code is in the bundle source.
  const loadSampleButton = import.meta.env.DEV && !preview && (
    <button
      type="button"
      onClick={loadSample}
      className="mb-3 btn-secondary py-1.5 text-xs"
    >
      Load sample data (dev only)
    </button>
  );

  const restoreBanner = restorePrompt && !preview && (
    <div className="callout-info mb-4 flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
      <div>
        <p className="font-medium">Restore your previous draft?</p>
        <p className="mt-1 text-brand-muted">
          We have an autosave from{" "}
          {restorePrompt.savedAt.toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
          })}{" "}
          (
          {restorePrompt.savedAt.toLocaleDateString([], {
            month: "short",
            day: "numeric",
          })}
          ). Sensitive fields (SSN, EIN, account, routing) will need to be
          re-entered.
        </p>
      </div>
      <div className="flex shrink-0 gap-2">
        <button
          type="button"
          onClick={applyRestore}
          className="btn-primary py-1.5 text-xs"
        >
          Restore
        </button>
        <button
          type="button"
          onClick={dismissRestore}
          className="btn-secondary py-1.5 text-xs"
        >
          Discard
        </button>
        <button
          type="button"
          onClick={() => setRestorePrompt(null)}
          aria-label="Dismiss restore prompt"
          className="rounded p-1 text-brand-muted hover:bg-brand-border/40 hover:text-brand-text"
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>
    </div>
  );

  const draftIndicator =
    !preview && draftSavedAt && !restorePrompt ? (
      <p
        className="mt-1 text-xs text-brand-muted"
        role="status"
        aria-live="polite"
      >
        Draft saved at{" "}
        {draftSavedAt.toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        })}
        .
      </p>
    ) : null;

  const header = (
    <div className="mb-6">
      <h1 className="text-2xl font-semibold tracking-tight text-white">
        New application
      </h1>
      <p className="mt-1.5 max-w-2xl text-sm leading-6 text-brand-muted">
        {sendMode === "send"
          ? "Email a tokenized intake link to a merchant or tech so they can fill in the application themselves. Their progress shows on the dashboard live."
          : "Full merchant onboarding application. Payment Products collects the complete application here and submits it to CardConnect; the merchant reviews and signs via the link we email them."}
        {applicationPath === "MNSP_ENROLLMENT_ONLY" && (
          <>
            {" "}
            This application communicates only the MAR + Enrollment Agreement
            and will not be submitted to CardConnect.
          </>
        )}
      </p>
      {draftIndicator}
    </div>
  );

  // Preview/demo: render standalone (no auth, no TeamLayout sidebar) so the
  // form can be pulled up directly. Only mounted on the dev-only
  // /preview/new-application route (see main.tsx).
  if (preview) {
    return (
      <div className="app-bg min-h-screen px-4 py-8">
        <div className="mx-auto max-w-5xl">
          <div className="callout-info mb-4 px-4 py-3">
            <strong>Preview mode.</strong> Nothing is submitted or stored.
            “Preview payload” shows the exact CreateApplicationInput that would
            be sent.
            <button
              type="button"
              onClick={loadSample}
              className="btn-secondary ml-3 px-2 py-1 text-xs"
            >
              Load sample data
            </button>
          </div>
          {header}
          {form}
        </div>
      </div>
    );
  }

  return (
    <div>
      <Link
        to="/applications"
        className="mb-4 inline-flex items-center gap-1 text-sm text-brand-muted transition-colors hover:text-brand-blue-bright"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to applications
      </Link>
      {header}
      {restoreBanner}
      {loadSampleButton}
      {form}
    </div>
  );
}
